import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AssetDetail } from "../api/assets";
import type { ApiError } from "../api/client";
import { QrCode } from "../components/QrCode";
import { assetQrUrl } from "../lib/assetCode";
import Ic from "../components/icons.jsx";

/**
 * `/asseti/:id/nalepnica` — printable label, rendered outside the AppShell. The QR carries
 * the label URL with the system id (§12); the tag stays the big human-readable line.
 * Everything but `.label-print` is hidden by `@media print`.
 */
export default function AssetLabelPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    assetsApi.get(id)
      .then((d) => { if (!cancelled) { setAsset(d); document.title = `${d.tag} — ${t("assets.label.title", "Nalepnica")}`; } })
      .catch((err: ApiError) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [id, t]);

  return (
    <div className="label-page">
      <div className="label-toolbar">
        <Link to={id ? `/asseti/${id}` : "/asseti"} className="btn" style={{ textDecoration: "none" }}>
          <Ic.arrowRight style={{ transform: "rotate(180deg)" }} /> {t("assets.label.back", "Nazad na karton")}
        </Link>
        <button type="button" className="btn primary lg" onClick={() => window.print()} disabled={!asset}>
          <Ic.print /> {t("assets.label.print", "Štampaj")}
        </button>
      </div>
      {error && <div className="card"><div className="empty" role="alert">{error}</div></div>}
      {!asset && !error && <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>}
      {asset && (
        <div className="label-print">
          <QrCode value={assetQrUrl(asset.inventoryNo)} size={220} alt={asset.inventoryNo} />
          <div className="label-text">
            <div className="label-tag">{asset.tag}</div>
            <div className="label-id mono">{asset.inventoryNo}</div>
            <div className="label-type mono">{asset.typeCode}</div>
            <div className="label-name">{asset.typeName}</div>
            {asset.serialNo && <div className="label-serial mono">S/N {asset.serialNo}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
